import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import http from 'highlight.js/lib/languages/http';
import ini from 'highlight.js/lib/languages/ini';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import yaml from 'highlight.js/lib/languages/yaml';

const LANGUAGES = {
  bash,
  http,
  ini,
  javascript,
  json,
  plaintext,
  python,
  yaml,
};
const COPIED_FOR_MS = 2000;
const ACTIVE_LINE = 0.3;
const ACTIVE_ZONE = `0px 0px -${(1 - ACTIVE_LINE) * 100}% 0px`;

Object.entries(LANGUAGES).forEach(([name, language]) =>
  hljs.registerLanguage(name, language)
);

const copyTimers = new WeakMap();
let observers = [];
let listening = false;

const languageOf = code => code.className.match(/\blanguage-(\S+)/)?.[1];

export const highlightCode = root => {
  root.querySelectorAll('pre code').forEach(code => {
    const language = languageOf(code);
    if (!language || !hljs.getLanguage(language) || code.dataset.highlighted)
      return;
    hljs.highlightElement(code);
  });
};

export const copyCode = async button => {
  const code = button.closest('[data-hc-code]').querySelector('pre');
  await navigator.clipboard.writeText(code.textContent.replace(/\n$/, ''));

  const label = button.querySelector('[data-hc-copy-label]');
  label.dataset.idleLabel ??= label.textContent;
  label.textContent = button.dataset.copiedLabel;
  button.dataset.copied = '';
  clearTimeout(copyTimers.get(button));
  copyTimers.set(
    button,
    setTimeout(() => {
      delete button.dataset.copied;
      label.textContent = label.dataset.idleLabel;
    }, COPIED_FOR_MS)
  );
};

export const sendFeedback = button => {
  const section = button.closest('[data-hc-feedback]');
  const helpful = button.dataset.hcFeedbackVote === 'true';
  fetch(section.dataset.url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ helpful }),
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => {});

  section.querySelector('[data-hc-feedback-ask]').hidden = true;
  const thanks = section.querySelector(
    `[data-hc-feedback-thanks="${helpful}"]`
  );
  thanks.hidden = false;
  thanks.focus();
};

const targetOf = link => decodeURIComponent(link.hash.slice(1));

const markActive = (links, id) => {
  links.forEach(link => {
    if (id && targetOf(link) === id)
      link.setAttribute('aria-current', 'location');
    else link.removeAttribute('aria-current');
  });
};

export const currentHeading = (body, headings) => {
  if (!headings.length) return null;
  if (body.getBoundingClientRect().bottom <= window.innerHeight)
    return headings[headings.length - 1];
  const line = window.innerHeight * ACTIVE_LINE;
  return (
    headings
      .filter(heading => heading.getBoundingClientRect().top <= line)
      .pop() || null
  );
};

const stopWatching = () => {
  observers.forEach(observer => observer.disconnect());
  observers = [];
};

const watchHeadings = body => {
  const links = [...document.querySelectorAll('[data-hc-toc-link]')];
  if (!links.length || !('IntersectionObserver' in window)) return;

  const headings = [...new Set(links.map(targetOf))]
    .map(id => document.getElementById(id))
    .filter(Boolean);
  const update = () => markActive(links, currentHeading(body, headings)?.id);

  const crossing = new IntersectionObserver(update, {
    rootMargin: ACTIVE_ZONE,
  });
  headings.forEach(heading => crossing.observe(heading));
  const ending = new IntersectionObserver(update);
  ending.observe(body.lastElementChild || body);
  observers = [crossing, ending];
  update();
};

const onClick = event => {
  const copy = event.target.closest('[data-hc-copy]');
  if (copy) {
    copyCode(copy).catch(() => {});
    return;
  }

  const vote = event.target.closest('[data-hc-feedback-vote]');
  if (vote) {
    sendFeedback(vote);
    return;
  }

  if (event.target.closest('[data-hc-contact]') && window.$chatwoot) {
    event.preventDefault();
    window.$chatwoot.toggle('open');
    return;
  }

  const tocLink = event.target.closest('[data-hc-toc-link]');
  const disclosure = tocLink?.closest('details[data-hc-toc-disclosure]');
  if (disclosure) disclosure.open = false;
};

export function initArticle() {
  if (!listening) {
    document.addEventListener('click', onClick);
    listening = true;
  }
  stopWatching();

  const body = document.querySelector('[data-hc-article-body]');
  if (!body) return;

  highlightCode(body);
  if (navigator.clipboard)
    body.querySelectorAll('[data-hc-copy]').forEach(button => {
      button.hidden = false;
    });
  document.querySelectorAll('[data-hc-feedback]').forEach(section => {
    section.hidden = false;
  });
  watchHeadings(body);
}
